import { useEffect, useState } from "react"

import { FileSearch, LoaderCircle, Upload } from "lucide-react"
import { useParams } from "react-router-dom"
import { toast } from "sonner"

import { LoadPreviousComparison } from "@/components/buttons/LoadPreviousComparison"
import { SimplifiedDocumentCard } from "@/components/cards/SimplifiedDocumentCard"
import { JsonDisplay } from "@/components/displays/JsonDisplay"
import { ShimmerText } from "@/components/indicators/LoadingIndicator"
import { DocumentSelectionOverlay } from "@/components/overlays/DocumentSelectionOverlay"
import { JSON_MAPPINGS } from "@/constants/mappings"
import { DocumentCompareDropdown } from "@/features/compare/DocumentCompareDropdown"
import { DocumentDropZone } from "@/features/compare/DocumentDropZone"
import { formatElapsedTime } from "@/lib/time"
import { Button } from "@ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tabs"
import { useUserSession } from "@session/UserSession"

import { ContentObjectItem, ExecutionRun } from "@vertesia/common"

const COMPARE_INTERACTION_ID = "67f520edcb6927c7b29ab021"

export function CompareDocumentsPanel() {
    const { id } = useParams()

    const { client } = useUserSession()

    const [loading, setLoading] = useState(false)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)
    const mapping = JSON_MAPPINGS[COMPARE_INTERACTION_ID]

    const [runId, setRunId] = useState<string | undefined>(undefined)
    const [referenceDocument, setReferenceDocument] = useState<ContentObjectItem | undefined>(undefined)
    const [comparativeDocuments, setComparativeDocuments] = useState<ContentObjectItem[]>([])

    const [result, setResult] = useState<any | undefined>(undefined)
    const [resultName, setResultName] = useState<string | undefined>(undefined)

    const searchDocument = async (id: string) => {
        return await client.objects.retrieve(id)
    }

    const uploadDocument = async (file: File): Promise<ContentObjectItem> => {
        try {
            return await client.objects.create({ type: undefined, content: file })
        } catch (error) {
            throw new Error(`Failed to upload ${file.name}: ${error}`)
        }
    }

    const handleReferenceDocumentDrop = async (files: File[]) => {
        if (files.length > 1) {
            toast.error("Only one reference document allowed")
            return
        }

        try {
            setLoading(true)
            const uploadedDocument = await uploadDocument(files[0])
            setReferenceDocument(uploadedDocument)
            toast.success(`Reference document "${files[0].name}" uploaded successfully`)
        } catch (error) {
            toast.error("Failed to upload reference document", {
                description: error instanceof Error ? error.message : "Unknown error",
                duration: 5000
            })
        } finally {
            setLoading(false)
        }
    }

    const handleComparativeDocumentsDrop = async (files: File[]) => {
        try {
            setLoading(true)
            const uploadPromises = files.map(file => uploadDocument(file))
            const uploadedDocuments = await Promise.all(uploadPromises)

            setComparativeDocuments(prev => [...prev, ...uploadedDocuments])
            toast.success(`${files.length} comparative document${files.length > 1 ? "s" : ""} uploaded successfully`)
        } catch (error) {
            toast.error("Failed to upload comparative documents", {
                description: error instanceof Error ? error.message : "Unknown error",
                duration: 5000
            })
        } finally {
            setLoading(false)
        }
    }

    const compareDocuments = () => {
        if (!referenceDocument) {
            return
        }

        setLoading(true)
        setResult(undefined)
        setResultName(undefined)
        setElapsedSeconds(0)

        client.interactions.execute(COMPARE_INTERACTION_ID, {
            data: {
                reference_document: `store:${referenceDocument.id}`,
                documents: [
                    ...comparativeDocuments.map(doc => `store:${doc.id}`)
                ]
            },
            tags: [`taskName: ${referenceDocument.name}`],
        })
            .then((response) => {
                setRunId(response.id)
                setResult(JSON.stringify(response.result?.[0].value))
                setResultName(`${referenceDocument.name}-report-${new Date().toISOString()}`)
            })
            .catch((error) => {
                toast.error("Error comparing documents", {
                    description: error.message,
                    duration: 5000
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const handleOnRunSelected = async (run: ExecutionRun) => {
        setRunId(run.id)

        const refDocId = run.parameters.reference_document?.replace("store:", "")
        const compDocIds = run.parameters.documents?.map((doc: string) => doc.replace("store:", "")) || []

        const refDoc = await searchDocument(refDocId)
        setReferenceDocument(refDoc)
        setComparativeDocuments(await Promise.all(compDocIds.map(async (docId: string) => await searchDocument(docId))))
        setResult(JSON.stringify(run.result?.[0].value))
        setResultName(`${refDoc.name}-report-${new Date().toISOString()}`)
    }

    const removeComparativeDocument = (documentId: string) => {
        setComparativeDocuments(prev => prev.filter(doc => doc.id !== documentId))
    }

    const searchDocumentComparison = (id: string) => {
        client.runs.retrieve(id)
            .then((response) => {
                handleOnRunSelected(response)
            })
            .catch((error) => {
                toast.error("Unable to load previous Documents comparison", {
                    description: error.message,
                    duration: 5000
                })
            })
    } 

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined

        if (loading) {
            setElapsedSeconds(0)
            interval = setInterval(() => {
                setElapsedSeconds(prev => prev + 1)
            }, 1000)
        } else {
            setElapsedSeconds(0)
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [loading])

    useEffect(() => {
        if (!client || !id) {
            return
        }

        searchDocumentComparison(id)
    }, [id])

    return (
        <div className="h-full w-full flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-gray-300 p-4 bg-gray-50">
                <div className='flex flex-col gap-0'>
                    <span className="text-lg font-medium">Compare Documents</span>
                    <span className="text-sm text-gray-600">Select Documents to track changes between resubmissions</span>
                </div>
                <LoadPreviousComparison onRunSelected={handleOnRunSelected} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 flex-1 min-h-0">
                <div className="lg:col-span-1 border-r border-gray-300 flex flex-col min-h-0">
                    <Tabs defaultValue="reference" className='flex-1 flex flex-col min-h-0'>
                        <div className="bg-gray-100 border-b border-gray-300 p-4 flex-shrink-0">
                            <TabsList className="bg-white grid grid-cols-2 gap-1">
                                <TabsTrigger value="reference">Reference Document</TabsTrigger>
                                <TabsTrigger value="comparative">Comparative Documents</TabsTrigger>
                            </TabsList>
                        </div>
                        <div className="flex-1 flex flex-col min-h-0">
                            <TabsContent value="reference" className="flex-1 flex flex-col gap-4 min-h-0 mt-0 p-4">
                                <div className='flex items-center justify-end flex-shrink-0'>
                                    <DocumentSelectionOverlay onObjSelection={setReferenceDocument}>
                                        <Button size="sm">
                                            <FileSearch className="size-4 mr-1" />
                                            Search Document
                                        </Button>
                                    </DocumentSelectionOverlay>
                                </div>
                                <DocumentDropZone
                                    onDrop={handleReferenceDocumentDrop}
                                    className="flex-1 min-h-0 overflow-hidden"
                                    multiple={false}
                                >
                                    <div className="h-full overflow-y-auto">
                                        {
                                            referenceDocument
                                                ? (
                                                    <SimplifiedDocumentCard object={referenceDocument} onRemoveFromSelection={() => setReferenceDocument(undefined)} />
                                                ) : (
                                                    <div className='h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6'>
                                                        <Upload className="size-8 text-gray-400 mb-2" />
                                                        <p className="text-gray-500 text-center">
                                                            Drag & drop reference document here
                                                        </p>
                                                        <p className="text-sm text-gray-400 text-center mt-1">
                                                            or use the search button above
                                                        </p>
                                                    </div>
                                                )
                                        }
                                    </div>
                                </DocumentDropZone>
                            </TabsContent>
                            <TabsContent value="comparative" className="flex-1 flex flex-col gap-4 min-h-0 mt-0 p-4">
                                <div className='flex items-center justify-end flex-shrink-0'>
                                    <DocumentSelectionOverlay onObjMultipleSelection={setComparativeDocuments}>
                                        <Button size="sm">
                                            <FileSearch className="size-4 mr-1" />
                                            Search Documents
                                        </Button>
                                    </DocumentSelectionOverlay>
                                </div>
                                <DocumentDropZone
                                    onDrop={handleComparativeDocumentsDrop}
                                    className="flex-1 min-h-0 overflow-hidden"
                                    multiple={true}
                                >
                                    <div className='h-full overflow-y-auto'>
                                        {
                                            comparativeDocuments.length > 0
                                                ? (
                                                    <div className="space-y-2">
                                                        {comparativeDocuments.map((document) => (
                                                            <SimplifiedDocumentCard key={document.id} object={document} onRemoveFromSelection={() => removeComparativeDocument(document.id)} />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className='h-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6'>
                                                        <Upload className="size-8 text-gray-400 mb-2" />
                                                        <p className="text-gray-500 text-center">
                                                            Drag & drop comparative documents here
                                                        </p>
                                                        <p className="text-sm text-gray-400 text-center mt-1">
                                                            or use the search button above
                                                        </p>
                                                    </div>
                                                )
                                        }
                                    </div>
                                </DocumentDropZone>
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
                <div className="lg:col-span-2 flex flex-col h-full overflow-hidden">
                    <div className="flex items-center justify-between bg-gray-100 border-b border-gray-300 p-4">
                        <h2 className="text-lg font-medium">Comparison Preview</h2>
                        <div className='flex gap-2'>
                            {
                                (result && resultName) && (
                                    <DocumentCompareDropdown runId={runId} content={result} filename={resultName} mapping={JSON_MAPPINGS["67f520edcb6927c7b29ab021"]} />
                                )
                            }
                            <Button onClick={compareDocuments} disabled={loading || !referenceDocument || !comparativeDocuments || !comparativeDocuments.length}>
                                {loading && <LoaderCircle className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" /> }
                                {loading ? "Comparing ..." : "Compare Documents"}
                            </Button>
                        </div>
                    </div>
                    <div className='flex-1 overflow-y-auto p-4'>
                        {
                            (result && mapping)
                                ? (
                                    <JsonDisplay content={result} mapping={mapping} />
                                ) : (
                                    loading
                                        ? (
                                            <div className='flex flex-col items-center justify-center h-full'>
                                                <ShimmerText>
                                                    <div className="flex items-center gap-2">
                                                        <LoaderCircle className="size-4 animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                                        <p className="text-gray-500 text-xl">Documents are being compared, please wait.</p>
                                                    </div>
                                                </ShimmerText>
                                                <p className="text-sm text-gray-400 mt-2">
                                                    Elapsed time: {formatElapsedTime(elapsedSeconds)}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className='flex flex-col items-center justify-center h-full'>
                                                <p className="text-gray-500">Select a reference Document and one or more comparative documents</p>
                                                <p className="text-gray-500">click the "Compare Documents" button to see the preview here.</p>
                                            </div>
                                        )
                                )
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}